import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader, ExternalLink, AlertCircle } from 'lucide-react';
import { apiGet, apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Alert, Spinner } from '../design-system';
import toast from 'react-hot-toast';

const GoogleIntegration = () => {
  const { userInfo } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  // No frontend environment variables needed - backend handles OAuth

  // Fetch Google connection status
  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await apiGet('/.netlify/functions/get-google-status', userInfo?.userId);
      setStatus(data);
    } catch (error) {
      console.error('Error fetching Google status:', error);
      toast.error('Failed to load Google integration status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo?.userId) {
      fetchStatus();
    }
  }, [userInfo?.userId]);

  // Handle Google OAuth connection
  const handleConnect = async () => {
    try {
      setConnecting(true);

      // Get OAuth URL from backend (which has the client ID)
      const response = await apiGet('/.netlify/functions/connect-google', userInfo?.userId);
      
      if (!response.authUrl) {
        throw new Error('Failed to get OAuth URL from server');
      }

      // Redirect to Google OAuth
      window.location.href = response.authUrl;
    } catch (error) {
      console.error('Error initiating Google OAuth:', error);
      toast.error(error.message || 'Failed to connect Google account');
      setConnecting(false);
    }
  };

  // Handle Google OAuth callback (redirect flow)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleConnected = urlParams.get('google_connected');
    const googleError = urlParams.get('google_error');

    if (googleConnected === 'true') {
      toast.success('Google account connected successfully!');
      fetchStatus();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (googleError) {
      const errorMessages = {
        'access_denied': 'Google authorization was cancelled',
        'unauthorized': 'You are not authorized to connect Google accounts',
        'connection_failed': 'Failed to connect Google account'
      };
      toast.error(errorMessages[googleError] || 'Google authorization failed');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Handle disconnect
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Google account? This will stop automatic Meet link creation for new bookings.')) {
      return;
    }

    try {
      setDisconnecting(true);
      await apiPost('/.netlify/functions/disconnect-google', {}, userInfo?.userId);
      toast.success('Google account disconnected successfully');
      await fetchStatus();
    } catch (error) {
      console.error('Error disconnecting Google account:', error);
      toast.error(error.message || 'Failed to disconnect Google account');
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Google Calendar Integration</h2>
        <p className="text-gray-600 mb-6">
          Connect your Google account to automatically create Google Meet links for therapy sessions.
          When patients book appointments, a Google Calendar event with a Meet link will be created in your calendar.
        </p>

        {status?.connected ? (
          <div className="space-y-4">
            <Alert variant="success" className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <div>
                <p className="font-semibold">Google account connected</p>
                {status.googleEmail && (
                  <p className="text-sm">{status.googleEmail}</p>
                )}
              </div>
            </Alert>

            {status.connectedAt && (
              <div className="text-sm text-gray-600">
                <p>Connected: {new Date(status.connectedAt).toLocaleDateString()}</p>
                {status.lastSyncedAt && (
                  <p>Last synced: {new Date(status.lastSyncedAt).toLocaleDateString()}</p>
                )}
              </div>
            )}

            <Button
              onClick={handleDisconnect}
              disabled={disconnecting}
              variant="danger"
              className="w-full sm:w-auto"
            >
              {disconnecting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect Google Account'
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <Alert variant="info" className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5" />
              <div>
                <p className="font-semibold">Not connected</p>
                <p className="text-sm">Connect your Google account to enable automatic Meet link creation.</p>
              </div>
            </Alert>

            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full sm:w-auto"
            >
              {connecting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect Google Account
                </>
              )}
            </Button>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="font-semibold mb-2">How it works:</h3>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>Connect your Google account (one-time setup)</li>
            <li>When patients book appointments, a Google Calendar event is automatically created</li>
            <li>A Google Meet link is included in the calendar event</li>
            <li>Both you and the patient receive the Meet link in confirmation emails</li>
            <li>You can disconnect at any time</li>
          </ul>
        </div>
      </div>
    </Card>
  );
};

export default GoogleIntegration;

